import {
  ArrowDownTrayIcon,
  ArrowLeftIcon, ArrowTopRightOnSquareIcon, BuildingOffice2Icon, ClipboardDocumentListIcon, IdentificationIcon, MapPinIcon, MinusIcon, PlusIcon, ShareIcon,
} from '@heroicons/react/24/outline';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { AsyncStatus } from '../../../../@types/AsyncStatus';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import { ROUTES } from '../../../../utils/routes';
import Badge from '../../../common/Badge';
import KPICard from '../../../common/KPICards';
import MUITable from '../../../common/MUITable';
import { showToast } from '../../../common/Notification';
import Popup from '../../../common/Popup/Popup.module';
import RedirectionCoproButtons from '../../../common/RedirectionCoproButtons';
import RedirectionMapButtons from '../../../common/RedirectionMapButtons/RedirectionMapButtons.module';
import Spinner from '../../../common/Spinner';
import PhysicalOwnersTable from '../components/PhysicalOwnersTable';
import SCIContactQualificationModal from '../components/SCIContactQualificationModal';
import type { SCIQualifiedContact } from '../components/SCIQualifiedContactsTable';
import SCIQualifiedContactsTable from '../components/SCIQualifiedContactsTable';
import SCIMap from '../components/SCIsMap';

interface SCIProperty {
  siren: string;
  denomination: string;
  nomCommune: string;
  numeroVoirie: string;
  indiceRepetitionVoirie: string;
  codeNatureVoie: string;
  libelleVoie: string;
  plotId: string;
  lettreBat: string | null;
  entreeEscalier: string | null;
  niveauLocal: number | null;
  numeroLocal: string | null;
  geometry: {
    type: string;
    coordinates: number[][][][];
  };
}

interface Owner {
  lastname: string;
  firstnames: string[];
  birthdayDate?: string;
  role?: string;
}

interface OwnerEntreprise {
  name: string;
  siren: string | null;
  role: string;
}

interface SCIData {
  siret: string;
  siren: string;
  name: string | null;
  denominationUsuelleEtablissement: string | null;
  denominationUniteLegale?: string | null;
  nomUniteLegale?: string | null;
  prenom1UniteLegale?: string | null;
  numeroVoieEtablissement: string;
  indiceRepetitionEtablissement: string | null;
  typeVoieEtablissement: string;
  libelleVoieEtablissement: string;
  codePostalEtablissement: string;
  libelleCommuneEtablissement: string;
  properties: SCIProperty[];
  owners: Owner[];
  ownersEntreprise: OwnerEntreprise[];
}
interface QualificationPayload {
  contactQualified?: boolean | null;
  targetedGestion?: boolean | null;
  targetedTransaction?: boolean | null;
  alreadyManaged?: boolean | null;
}

interface TargetingQualification {
  contactQualified?: boolean | null;
  targetedGestion?: boolean | null;
  targetedTransaction?: boolean | null;
  alreadyManaged?: boolean | null;
}

interface SCILocalQualification {
  plotId: string;
  numeroLocal: string;
  usage: 'residentiel' | 'tertiaire' | 'autre' | null;
}

interface SCICopro {
  id: number;
  name: string | null;
  remoteId: string | null;
  address: string | null;
  cp: string | null;
  city: string | null;
}

const LOCAL_USAGE_OPTIONS = [
  { value: '', label: '-' },
  { value: 'residentiel', label: 'Résidentiel' },
  { value: 'tertiaire', label: 'Tertiaire' },
  { value: 'autre', label: 'Autre' },
] as const;

const headers = [
  { title: 'Parcelle' },
  { title: 'Adresse' },
  { title: 'Commune' },
  { title: 'Batiment' },
  { title: 'Entrée/Escalier' },
  { title: 'Niveau' },
  { title: 'Numéro de local' },
  { title: 'Usage du local' },
];

const getDisplayName = (sci: SCIData): string => {
  const isDefined = (v?: string | null) => !!v && v !== '[ND]';
  if (isDefined(sci.denominationUniteLegale)) { return sci.denominationUniteLegale as string; }
  if (isDefined(sci.prenom1UniteLegale) && isDefined(sci.nomUniteLegale)) { return `${sci.prenom1UniteLegale as string} ${sci.nomUniteLegale as string}`; }
  if (isDefined(sci.denominationUsuelleEtablissement)) { return sci.denominationUsuelleEtablissement as string; }
  if (isDefined(sci.name)) { return sci.name as string; }
  return `SCI ${sci.siren}`;
};

const SirenSCIs: React.FC = () => {
  const { siren } = useParams<{ siren: string }>();
  const navigate = useNavigate();
  const [sciData, setSciData] = useState<SCIData | null>(null);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAllOwners, setShowAllOwners] = useState(false);
  const [showAllCompanyOwners, setShowAllCompanyOwners] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState<string | null>(null);

  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const [qualifiedContact, setQualifiedContact] = useState<SCIQualifiedContact | null>(null);
  const [targetingQualification, setTargetingQualification] = useState<TargetingQualification | null>(null);
  const [isQualifLoading, setIsQualifLoading] = useState<boolean>(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isTargetGestionOpen, setIsTargetGestionOpen] = useState(false);
  const [isTargetTransactionOpen, setIsTargetTransactionOpen] = useState(false);
  const [isGestionLoading, setIsGestionLoading] = useState(false);
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);
  const [isManagedLoading, setIsManagedLoading] = useState(false);
  const [localQualifications, setLocalQualifications] = useState<Record<string, SCILocalQualification>>({});
  const [savingLocalKey, setSavingLocalKey] = useState<string | null>(null);
  const [coproList, setCoproList] = useState<SCICopro[]>([]);
  const [coproLoading, setCoproLoading] = useState(false);

  useEffect(() => {
    if (!siren || !jwt) {return;}

    const fetchSCIData = async() => {
      setStatus('PENDING');
      try {
        const response = await fetch(`/api/scis/${siren}`, JWTHeader(jwt));

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: SCIData = await response.json();
        setSciData(data);
        setStatus('SUCCESS');
      } catch (error) {
        console.error('Error fetching SCI data :', error);
        setStatus('ERROR');
        setErrorMessage('Erreur lors du chargement des données de la SCI');
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
      }
    };

    if (siren) {
      fetchSCIData();
    }
  }, [siren, jwt]);

  useEffect(() => {
    const fetchQualification = async() => {
      if (!company?.id || !jwt || !siren) {return;}
      setIsQualifLoading(true);
      try {
        const r = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification`, JWTHeader(jwt));
        if (r.ok) {
          const data = await r.json();
          setQualifiedContact(data?.contact || null);
          setTargetingQualification({
            contactQualified: data?.contactQualified,
            targetedGestion: data?.targetedGestion,
            targetedTransaction: data?.targetedTransaction,
            alreadyManaged: data?.alreadyManaged,
          });
        }
      } catch {
        // noop
      } finally {
        setIsQualifLoading(false);
      }
    };
    fetchQualification();
  }, [company?.id, jwt, siren]);

  useEffect(() => {
    const fetchLocalQualifications = async() => {
      if (!company?.id || !jwt || !siren) {return;}
      try {
        const r = await fetch(`/api/companies/${company.id}/scis/${siren}/locals`, JWTHeader(jwt));
        if (r.ok) {
          const data = await r.json();
          const locals: SCILocalQualification[] = data?.locals || [];
          const mapped: Record<string, SCILocalQualification> = {};
          locals.forEach((l) => {
            const key = `${l.plotId}::${l.numeroLocal}`;
            mapped[key] = l;
          });
          setLocalQualifications(mapped);
        }
      } catch {
        // noop
      }
    };
    fetchLocalQualifications();
  }, [company?.id, jwt, siren]);

  useEffect(() => {
    const fetchCopros = async() => {
      if (!company?.id || !jwt || !siren) {return;}
      setCoproLoading(true);
      try {
        const r = await fetch(`/api/companies/${company.id}/scis/${siren}/copros`, JWTHeader(jwt));
        if (r.ok) {
          const data: SCICopro[] = await r.json();
          setCoproList(data);
        }
      } catch {
        // noop
      } finally {
        setCoproLoading(false);
      }
    };
    fetchCopros();
  }, [company?.id, jwt, siren]);

  const patchLocalQualification = async(plotId: string, numeroLocal: string, usage: string | null) => {
    if (!company?.id || !jwt || !siren) {return false;}
    const key = `${plotId}::${numeroLocal}`;
    setSavingLocalKey(key);
    try {
      const r = await fetch(`/api/companies/${company.id}/scis/${siren}/locals/${encodeURIComponent(plotId)}/${encodeURIComponent(numeroLocal)}`, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ usage: usage || null }),
      });
      if (r.ok) {
        setLocalQualifications(prev => ({
          ...prev,
          [key]: { plotId, numeroLocal, usage: (usage as SCILocalQualification['usage']) || null },
        }));
        showToast('success', 'Usage du local enregistré.');
        return true;
      }
      throw new Error('save failed');
    } catch {
      showToast('error', 'Impossible de sauvegarder l\'usage du local.');
      return false;
    } finally {
      setSavingLocalKey(null);
    }
  };

  const patchQualification = async(payload: QualificationPayload) => {
    if (!company?.id || !jwt || !siren) {return false;}
    const r = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification`, {
      method: 'PATCH',
      headers: {
        Authorization: JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return r.ok;
  };

  const handleGestionTarget = async(value: boolean) => {
    setIsGestionLoading(true);
    try {
      const ok = await patchQualification({ targetedGestion: value });
      if (ok) {
        setTargetingQualification(prev => prev ? { ...prev, targetedGestion: value } : { targetedGestion: value });
        showToast('success', 'Qualification gestion locative enregistrée.');
      } else {
        throw new Error('update failed');
      }
    } catch {
      showToast('error', 'Impossible de sauvegarder.');
    } finally {
      setIsGestionLoading(false);
    }
  };

  const handleTransactionTarget = async(value: boolean) => {
    setIsTransactionLoading(true);
    try {
      const ok = await patchQualification({ targetedTransaction: value });
      if (ok) {
        setTargetingQualification(prev => prev ? { ...prev, targetedTransaction: value } : { targetedTransaction: value });
        showToast('success', 'Qualification transaction enregistrée.');
      } else {
        throw new Error('update failed');
      }
    } catch {
      showToast('error', 'Impossible de sauvegarder.');
    } finally {
      setIsTransactionLoading(false);
    }
  };

  const handleAlreadyManaged = async(value: boolean | null) => {
    setIsManagedLoading(true);
    try {
      const ok = await patchQualification({ alreadyManaged: value });
      if (!ok) {
        throw new Error('update failed');
      }
      setTargetingQualification(prev => ({ ...(prev || {}), alreadyManaged: value }));
      showToast('success', 'Statut client en gestion enregistré.');
    } catch {
      showToast('error', 'Impossible de sauvegarder.');
    } finally {
      setIsManagedLoading(false);
    }
  };

  const handleDeleteQualifiedContact = async() => {
    if (!company?.id || !jwt || !siren) {return;}
    try {
      const r = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification/contact`, {
        method: 'DELETE',
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!r.ok) { throw new Error('delete failed'); }
      setQualifiedContact(null);
      showToast('success', 'Contact supprimé.');
    } catch {
      showToast('error', 'Suppression du contact impossible.');
    }
  };

  const renderPropertyTable = () => {
    if (!sciData || !sciData.properties) {return null;}

    const sortedProperties = [...sciData.properties].sort((a, b) => {
      if (a.plotId === selectedParcel) {return -1;}
      if (b.plotId === selectedParcel) {return 1;}
      return a.plotId.localeCompare(b.plotId, undefined, { numeric: true, sensitivity: 'base' });
    });

    return (
      <MUITable
        data={sortedProperties.map((property) => {
          const fullAddress = `${property.numeroVoirie} ${property.indiceRepetitionVoirie || ''} ${property.codeNatureVoie} ${property.libelleVoie}, ${property.nomCommune}`;
          const propertyAddress = `${property.numeroVoirie} ${property.indiceRepetitionVoirie || ''} ${property.codeNatureVoie} ${property.libelleVoie}`;
          return {
            Adresse: <div className="flex flex-row">
              <RedirectionMapButtons address={fullAddress} />
              {propertyAddress}
            </div>,
            Commune: property.nomCommune,
            Parcelle: (
              <div className="flex items-center">
                {property.geometry?.coordinates?.length > 0 ? (
                  <button
                    onClick={() => setSelectedParcel(property.plotId)}
                    className="mr-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
                  >
                    <MapPinIcon className="h-5 w-5" />
                  </button>
                ) : null}
                <div>
                  {property.plotId}
                  {(!property.geometry?.coordinates?.length) && (
                    <div className="text-[0.65rem] text-gray-400">Parcelle non géolocalisée dans le cadastre</div>
                  )}
                </div>
              </div>
            ),
            Batiment: property.lettreBat || '-',
            'Entrée/Escalier': property.entreeEscalier || '-',
            Niveau: property.niveauLocal !== null ? property.niveauLocal.toString() : '-',
            'Numéro de local': property.numeroLocal || '-',
            'Usage du local': (() => {
              const key = `${property.plotId}::${property.numeroLocal || ''}`;
              const qual = localQualifications[key];
              const currentUsage = qual?.usage || '';
              const isSaving = savingLocalKey === key;
              return (
                <select
                  className="min-w-[120px] rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-50"
                  value={currentUsage}
                  disabled={isSaving}
                  onChange={(e) => {
                    patchLocalQualification(property.plotId, property.numeroLocal || '', e.target.value || null);
                  }}
                >
                  {LOCAL_USAGE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              );
            })(),
          };
        })}
        headers={headers}
        tableClass="min-w-full bg-white border text-sm"
        isSearchable
        customRowClass={(row) => (React.isValidElement(row.Parcelle) && row.Parcelle.props?.children?.[1] === selectedParcel ? 'bg-yellow-full' : '')}
      />
    );
  };

  const calculateAge = (birthdayDate: string): number | null => {
    if (!birthdayDate) {return null;}
    
    const today = new Date();
    const birthDate = new Date(birthdayDate);
    
    if (isNaN(birthDate.getTime())) {return null;}
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const renderOwnerTable = () => {
    if (!sciData || !sciData.owners) {return null;}

    if (sciData.owners.length === 0) {
      return (
        <div className="mt-4">
          <Badge mode="warning" text="Aucun propriétaire physique trouvé dans les données INPI" />
        </div>
      );
    }

    const ownersToShow = showAllOwners ? sciData.owners : sciData.owners.slice(0, 2);

    return (
      <div className="overflow-x-scroll">
        <PhysicalOwnersTable owners={ownersToShow} />
        {sciData.owners.length > 2 && (
          <div className="mt-4 flex w-full justify-center">
            <button
              className="flex w-full items-center justify-center rounded border border-gray-600 px-3 py-1 text-gray-600 transition-colors hover:bg-gray-600 hover:text-white md:w-auto"
              onClick={() => setShowAllOwners(!showAllOwners)}
            >
              {showAllOwners ? (
                <>
                  <MinusIcon className="mr-2 h-5 w-5" />
                  Voir moins
                </>
              ) : (
                <>
                  <PlusIcon className="mr-2 h-5 w-5" />
                  Voir tous les propriétaires
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCompanyOwnerTable = () => {
    if (!sciData || !sciData.ownersEntreprise) {return null;}

    if (sciData.ownersEntreprise.length === 0) {
      return (
        <div className="mt-4">
          <Badge mode="warning" text="Aucun propriétaire moral trouvé dans les données INPI" />
        </div>
      );
    }

    const companyOwnersToShow = showAllCompanyOwners ? sciData.ownersEntreprise : sciData.ownersEntreprise.slice(0, 2);

    return (
      <div className="overflow-x-scroll">
        <table className="min-w-full divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Nom</th>
              <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Rôle</th>
              <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Siren</th>
              <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {companyOwnersToShow.map((companyOwner, index) => (
              <tr key={index}>
                <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                  {companyOwner.name}
                </td>
                <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                  {companyOwner.role || '-'}
                </td>
                <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                  {companyOwner.siren || '-'}
                </td>
                <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                  {companyOwner.siren && (
                    <button
                      onClick={() => window.open(`https://www.pappers.fr/entreprise/${companyOwner.siren}`, '_blank', 'noopener,noreferrer')}
                      className="flex items-center text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
                    >
                      <ArrowTopRightOnSquareIcon className="mr-1 h-4 w-4" />
                      Voir sur Pappers
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sciData.ownersEntreprise.length > 2 && (
          <div className="mt-4 flex w-full justify-center">
            <button
              className="flex w-full items-center justify-center rounded border border-gray-600 px-3 py-1 text-gray-600 transition-colors hover:bg-gray-600 hover:text-white md:w-auto"
              onClick={() => setShowAllCompanyOwners(!showAllCompanyOwners)}
            >
              {showAllCompanyOwners ? (
                <>
                  <MinusIcon className="mr-2 h-5 w-5" />
                  Voir moins
                </>
              ) : (
                <>
                  <PlusIcon className="mr-2 h-5 w-5" />
                  Voir toutes les entreprises
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleShareLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      showToast('success', 'Lien de la SCI copié dans le presse-papiers.');
    }).catch((err) => {
      console.error('Failed to copy: ', err);
      showToast('error', 'Échec de la copie du lien de la SCI.');
    });
  };

  const exportToExcel = async() => {
    if (!sciData || !sciData.properties) {
      showToast('warning', 'Aucune donnée à exporter.');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('SCI Data');

    const headerStyle: Partial<ExcelJS.Style> = {
      font: { bold: true, color: { argb: 'FFFFFFFF' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: {
        top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
      },
    };
    const subHeaderStyle: Partial<ExcelJS.Style> = {
      font: { bold: true },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } },
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: {
        top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
      },
    };
    const normalCellStyle: Partial<ExcelJS.Style> = {
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: {
        top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
      },
    };

    worksheet.addRow(['Informations SCI']).eachCell((cell) => Object.assign(cell, headerStyle));
    worksheet.addRow(['Nom de la SCI', getDisplayName(sciData)]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['Dénomination', sciData.denominationUsuelleEtablissement || getDisplayName(sciData)]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['SIREN', sciData.siren]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['Adresse', `${sciData.numeroVoieEtablissement} ${sciData.indiceRepetitionEtablissement || ''} ${sciData.typeVoieEtablissement} ${sciData.libelleVoieEtablissement}, ${sciData.codePostalEtablissement} ${sciData.libelleCommuneEtablissement}`]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['Nombre de biens', sciData.properties.length.toString()]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['Nombre de propriétaires physiques', sciData.owners.length.toString()]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow(['Nombre de propriétaires morales', (sciData.ownersEntreprise ? sciData.ownersEntreprise.length : 0).toString()]).eachCell((cell, colNumber) => {
      Object.assign(cell, colNumber === 1 ? subHeaderStyle : normalCellStyle);
    });
    worksheet.addRow([]);

    worksheet.addRow(['Propriétaires - Personnes physiques', '', '', '']).eachCell((cell) => Object.assign(cell, headerStyle));
    worksheet.addRow(['Nom', 'Prénom(s)', 'Date de naissance', 'Rôle']).eachCell((cell) => Object.assign(cell, subHeaderStyle));
    sciData.owners.forEach((owner) => {
      worksheet.addRow([
        owner.lastname, 
        owner.firstnames.join(', '), 
        owner.birthdayDate ? `${owner.birthdayDate} ${calculateAge(owner.birthdayDate) !== null ? `(${calculateAge(owner.birthdayDate)} ans)` : ''}` : '-',
        owner.role || '-',
      ]).eachCell((cell) => Object.assign(cell, normalCellStyle));
    });
    worksheet.addRow([]);

    if (sciData.ownersEntreprise && sciData.ownersEntreprise.length > 0) {
      worksheet.addRow(['Propriétaires - Personnes morales', '', '', '']).eachCell((cell) => Object.assign(cell, headerStyle));
      worksheet.addRow(['Nom', 'Rôle', 'SIREN', '']).eachCell((cell) => Object.assign(cell, subHeaderStyle));
      sciData.ownersEntreprise.forEach((companyOwner) => {
        worksheet.addRow([
          companyOwner.name,
          companyOwner.role || '-',
          companyOwner.siren || '-',
          '',
        ]).eachCell((cell) => Object.assign(cell, normalCellStyle));
      });
      worksheet.addRow([]);
    }

    worksheet.addRow(['Biens', '', '', '', '', '', '']).eachCell((cell) => Object.assign(cell, headerStyle));
    worksheet.addRow(['Parcelle', 'Adresse', 'Commune', 'Bâtiment', 'Entrée/Escalier', 'Niveau', 'Numéro de local']).eachCell((cell) => Object.assign(cell, subHeaderStyle));
    sciData.properties.forEach((property) => {
      worksheet.addRow([
        property.plotId,
        `${property.numeroVoirie} ${property.indiceRepetitionVoirie || ''} ${property.codeNatureVoie} ${property.libelleVoie}`,
        property.nomCommune,
        property.lettreBat || '-',
        property.entreeEscalier || '-',
        property.niveauLocal !== null ? property.niveauLocal.toString() : '-',
        property.numeroLocal || '-',
      ]).eachCell((cell) => Object.assign(cell, normalCellStyle));
    });

    worksheet.columns.forEach((column, index) => {
      column.width = [20, 40, 20, 15, 15, 10, 15][index] || 15;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `SCI_${sciData.siren}_${new Date().toISOString()}.xlsx`;
    link.click();
  };

  const handleCopySiren = () => {
    navigator.clipboard.writeText(sciData.siren).then(() => {
      showToast('success', 'Numéro SIREN copié dans le presse-papiers.');
    });
  };

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="items-center justify-between md:flex">
          <div className="flex items-center">
            <button
              onClick={handleGoBack}
              className="mr-4 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">
                Biens de la SCI :{' '}
                {sciData ? getDisplayName(sciData) : siren}
              </h1>
              {sciData && sciData.siren && (
                <p className="mt-2 mb-2 ml-0 flex items-center text-sm text-gray-500 md:mt-1 md:mb-0">
                  SIREN :
                  {' '}
                  {sciData.siren}
                  <button
                    onClick={handleCopySiren}
                    className="ml-2 rounded-full border border-gray-500 p-0.5 text-gray-500 hover:bg-gray-500 hover:text-white"
                    title="Copier le SIREN"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4" />
                  </button>
                </p>
              )}
            </div>
          </div>
          <div className="items-center md:flex md:space-x-4">
            <button
              onClick={handleShareLink}
              className="text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
              title="Partager le lien"
            >
              <ShareIcon className="h-6 w-6" />
            </button>
            <div className="flex flex-col md:space-y-2">
              <a
                href={`https://www.pappers.fr/entreprise/${siren}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex w-full items-center justify-center rounded border border-rgpd-blue px-3 py-1 text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white md:mt-0 md:w-auto"
              >
                <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
                Voir sur Pappers
              </a>
              <button
                onClick={exportToExcel}
                className="mt-2 flex w-full items-center justify-center rounded border border-rgpd-green px-3 py-1 text-rgpd-green transition-colors hover:bg-rgpd-green hover:text-white md:mt-0 md:w-auto"
              >
                <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
                Exporter
              </button>
            </div>
          </div>
        </div>
        {status === 'SUCCESS' && sciData && (
          <div className="my-10">
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
              <KPICard
                icon={<MapPinIcon className="h-6 w-6" />}
                title="Adresse"
                value={`${sciData.numeroVoieEtablissement || ''} ${sciData.indiceRepetitionEtablissement || ''} ${sciData.typeVoieEtablissement || ''} ${sciData.libelleVoieEtablissement || ''}, ${sciData.codePostalEtablissement || ''} ${sciData.libelleCommuneEtablissement || ''}`}
              />
              <KPICard
                icon={<IdentificationIcon className="h-6 w-6" />}
                title="Nombre de propriétaires"
                value={`Personnes physiques : ${sciData.owners.length} | Personnes morales : ${sciData.ownersEntreprise ? sciData.ownersEntreprise.length : 0}`}
              />
            </div>
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold">
                <BuildingOffice2Icon className="mr-2 inline-block h-6 w-6" />
                Copropriétés liées
                {!coproLoading && ` (${coproList.length})`}
              </h2>
              {coproLoading && (
                <Skeleton count={2} height={50} className="mb-2" />
              )}
              {!coproLoading && coproList.length === 0 && (
                <Badge mode="warning" text="Aucune copropriété trouvée pour cette SCI" />
              )}
              {!coproLoading && coproList.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Copropriété</th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Adresse</th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Identifiant RNIC</th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {coproList.map((copro) => (
                        <tr key={copro.id}>
                          <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                            <div className="flex items-center">
                              <RedirectionCoproButtons coproId={copro.id} />
                              {copro.name || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                            {[copro.address, copro.cp, copro.city].filter(Boolean).join(', ') || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                            {copro.remoteId || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm whitespace-nowrap">
                            <Link
                              to={ROUTES.ANALYZING_COPRO_MANAGING_ID.replace(':coproId', copro.id.toString())}
                              className="flex items-center text-rgpd-blue transition-colors hover:text-rgpd-blue-dark hover:underline"
                            >
                              <ArrowTopRightOnSquareIcon className="mr-1 h-4 w-4" />
                              Voir la fiche copro
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold">Contact qualifié</h2>
              <div className="mb-4">
                <SCIQualifiedContactsTable
                  contacts={qualifiedContact ? [qualifiedContact] : []}
                  variant="large"
                  showActions={!!qualifiedContact}
                  onDeleteContact={() => handleDeleteQualifiedContact()}
                />
                {isQualifLoading && (
                  <div className="mt-2 text-xs text-gray-500">Chargement...</div>
                )}
                <div className="mt-3 flex items-center justify-center">
                  <button
                    className="rounded border border-orange-500 px-3 py-1 text-orange-600 hover:bg-orange-500 hover:text-white"
                    onClick={async() => {
                      setIsContactOpen(true);
                      // ensure latest qualification is loaded to drive modal
                      if (!company?.id || !jwt || !siren) {return;}
                      setIsQualifLoading(true);
                      try {
                        const r = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification/contact`, JWTHeader(jwt));
                        if (r.ok) {
                          const data = await r.json();
                          setQualifiedContact((data && (data.civilite || data.nom || data.prenom || data.email || data.telephone || data.adresse)) ? data : null);
                        }
                      } finally {
                        setIsQualifLoading(false);
                      }
                    }}
                  >
                    Qualifier le contact de la SCI
                  </button>
                </div>
              </div>
            </div>
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold">Ciblage de la SCI</h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                <div className="rounded-lg border bg-white p-4">
                  <h3 className="mb-3 text-lg font-semibold">Gestion locative</h3>
                  <div className="mb-3 flex items-center gap-3">
                    <span className="text-sm text-gray-600">Statut :</span>
                    <span className={`rounded-full px-4 py-2 text-sm font-medium ${
                      targetingQualification?.targetedGestion
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {targetingQualification?.targetedGestion ? 'Ciblé' : 'Non ciblé'}
                    </span>
                  </div>
                  {targetingQualification?.alreadyManaged === true && (
                    <p className="mb-3 text-xs text-gray-600">Cette SCI est déjà cliente en gestion chez vous.</p>
                  )}
                  <button
                    className="flex w-full items-center justify-center rounded border border-teal-600 px-3 py-2 text-teal-600 transition-colors hover:bg-teal-600 hover:text-white"
                    onClick={() => setIsTargetGestionOpen(true)}
                  >
                    Modifier le ciblage gestion
                  </button>
                </div>
                <div className="rounded-lg border bg-white p-4">
                  <h3 className="mb-3 text-lg font-semibold">Transaction</h3>
                  <div className="mb-3 flex items-center gap-3">
                    <span className="text-sm text-gray-600">Statut :</span>
                    <span className={`rounded-full px-4 py-2 text-sm font-medium ${
                      targetingQualification?.targetedTransaction
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {targetingQualification?.targetedTransaction ? 'Ciblé' : 'Non ciblé'}
                    </span>
                  </div>
                  <button
                    className="flex w-full items-center justify-center rounded border border-indigo-600 px-3 py-2 text-indigo-600 transition-colors hover:bg-indigo-600 hover:text-white"
                    onClick={() => setIsTargetTransactionOpen(true)}
                  >
                    Modifier le ciblage transaction
                  </button>
                </div>
                <div className="rounded-lg border bg-white p-4">
                  <h3 className="mb-3 text-lg font-semibold">Client en gestion</h3>
                  <div className="mb-3 flex items-center gap-3">
                    <span className="text-sm text-gray-600">Statut :</span>
                    <span className={`rounded-full px-4 py-2 text-sm font-medium ${
                      targetingQualification?.alreadyManaged === true
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {targetingQualification?.alreadyManaged === true && 'Déjà client'}
                      {targetingQualification?.alreadyManaged === false && 'Pas client'}
                      {(targetingQualification?.alreadyManaged ?? null) === null && 'Non renseigné'}
                    </span>
                    {isManagedLoading && <Spinner size="sm" />}
                  </div>
                  <p className="mb-3 text-xs text-gray-600">La SCI vous confie-t-elle déjà la gestion de biens ?</p>
                  <div className="grid grid-cols-2 gap-2" role="group" aria-label="Client en gestion">
                    {([[true, 'Oui'], [false, 'Non']] as [boolean, string][]).map(([value, label]) => (
                      <button
                        key={label}
                        type="button"
                        aria-pressed={targetingQualification?.alreadyManaged === value}
                        disabled={isManagedLoading}
                        onClick={() => handleAlreadyManaged(value)}
                        className={`rounded border px-3 py-2 transition-colors disabled:opacity-50 ${
                          targetingQualification?.alreadyManaged === value
                            ? 'border-emerald-700 bg-emerald-700 text-white'
                            : 'border-emerald-700 text-emerald-700 hover:bg-emerald-50'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {(targetingQualification?.alreadyManaged ?? null) !== null && (
                    <button
                      type="button"
                      disabled={isManagedLoading}
                      onClick={() => handleAlreadyManaged(null)}
                      className="mt-2 text-xs text-gray-500 underline hover:text-gray-700 disabled:opacity-50"
                    >
                      Effacer la réponse
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold">
                Personnes physiques (
                {sciData.owners.length}
                )
              </h2>
              {renderOwnerTable()}
            </div>
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold">
                Personnes morales (
                {sciData.ownersEntreprise ? sciData.ownersEntreprise.length : 0}
                )
              </h2>
              {renderCompanyOwnerTable()}
            </div>
            <div className="grid grid-cols-1 gap-10">
              <div className="flex flex-col justify-start">
                <h2 className="mb-4 text-xl font-bold">
                  Détails des biens (
                  {sciData.properties.length}
                  )
                </h2>
                                <div className="mb-6 rounded-lg border bg-gray-50 p-4">
                  <h3 className="mb-3 text-lg font-semibold">Répartition des usages des locaux</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                    {(() => {
                      const counts = { residentiel: 0, tertiaire: 0, autre: 0, undefined: 0 };
                      sciData.properties.forEach((property) => {
                        const key = `${property.plotId}::${property.numeroLocal || ''}`;
                        const qual = localQualifications[key];
                        const usage = qual?.usage;
                        if (usage === 'residentiel') {
                          counts.residentiel++;
                        } else if (usage === 'tertiaire') {
                          counts.tertiaire++;
                        } else if (usage === 'autre') {
                          counts.autre++;
                        } else {
                          counts.undefined++;
                        }
                      });
                      return (
                        <>
                          <div className="rounded border bg-white px-3 py-2 text-center">
                            <div className="font-bold text-green-600">{counts.residentiel}</div>
                            <div className="text-gray-600">Résidentiel</div>
                          </div>
                          <div className="rounded border bg-white px-3 py-2 text-center">
                            <div className="font-bold text-blue-600">{counts.tertiaire}</div>
                            <div className="text-gray-600">Tertiaire</div>
                          </div>
                          <div className="rounded border bg-white px-3 py-2 text-center">
                            <div className="font-bold text-orange-600">{counts.autre}</div>
                            <div className="text-gray-600">Autre</div>
                          </div>
                          <div className="rounded border bg-white px-3 py-2 text-center">
                            <div className="font-bold text-gray-500">{counts.undefined}</div>
                            <div className="text-gray-600">Non défini</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                {renderPropertyTable()}
              </div>
              <SCIMap
                copros={[{
                  id: 1,
                  name: getDisplayName(sciData),
                  scis: {
                    total: 1,
                    plots: sciData.properties.map((property) => ({
                      siren: property.siren,
                      plotId: property.plotId,
                      geometry: {
                        type: property.geometry.type,
                        coordinates: property.geometry.coordinates,
                      },
                    })),
                  },
                }]}
                onParcelClick={() => {}}
                selectedParcel={selectedParcel}
                setSelectedParcel={setSelectedParcel}
              />
            </div>
          </div>
        )}
        {status === 'ERROR' && (
          <div className="my-10 text-center">
            <Badge mode="error" text={errorMessage} />
          </div>
        )}
        {status === 'PENDING' && (
          <div className="my-10 text-center">
            <Spinner size="xl" />
          </div>
        )}
        <SCIContactQualificationModal
          isOpen={isContactOpen}
          onClose={() => setIsContactOpen(false)}
          siren={siren}
          sciName={sciData ? getDisplayName(sciData) : siren}
          initialContact={qualifiedContact}
          initialTargets={{
            targetedGestion: targetingQualification?.targetedGestion ?? null,
            targetedTransaction: targetingQualification?.targetedTransaction ?? null,
          }}
          isSaving={isSavingContact}
          isLoading={isQualifLoading}
          onSave={async(contact, targets) => {
            setIsSavingContact(true);
            try {
              const r = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification/contact`, {
                method: 'PUT',
                headers: { Authorization: JWTAuthorization(jwt), 'Content-Type': 'application/json' },
                body: JSON.stringify(contact),
              });
              if (!r.ok) { throw new Error('save failed'); }
              setQualifiedContact(contact);
            } catch {
              showToast('error', 'Enregistrement du contact impossible.');
              setIsSavingContact(false);
              return false;
            }

            const targetsSaved = !targets || await patchQualification(targets).catch(() => false);
            if (targets && targetsSaved) {
              setTargetingQualification((prev) => ({ ...(prev || {}), ...targets }));
            }
            setIsSavingContact(false);
            if (!targetsSaved) {
              // The contact is saved: keep the modal open so that the targets can be saved again
              showToast('warning', "Contact enregistré, mais les cibles n'ont pas pu l'être. Réessayez.");
              return false;
            }
            showToast('success', 'Contact enregistré avec succès.');

            return true;
          }}
          onDelete={qualifiedContact ? async() => {
            try {
              const r = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification/contact`, {
                method: 'DELETE',
                headers: { Authorization: JWTAuthorization(jwt) },
              });
              if (!r.ok) { throw new Error('delete failed'); }
              setQualifiedContact(null);
              showToast('success', 'Contact supprimé.');
            } catch {
              showToast('error', 'Suppression du contact impossible.');
            }
          } : undefined}
        />

        {/* Target Gestion Modal */}
        <Popup isOpen={isTargetGestionOpen} onClose={() => setIsTargetGestionOpen(false)} title={<span>Qualifier le ciblage de la SCI</span>} additionalClass="w-full max-w-lg">
          <div className="space-y-4 text-sm">
            <div className="font-semibold text-gray-700">{sciData ? getDisplayName(sciData) : `SCI ${siren}`}</div>
            <div className="space-y-2">
              <button
                className="flex w-full items-center justify-center rounded border px-3 py-2 hover:bg-teal-50"
                onClick={() => { if (!isGestionLoading) { handleGestionTarget(false); setIsTargetGestionOpen(false); } }}
                disabled={isGestionLoading}
              >
                {isGestionLoading ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <span role="img" aria-label="cross mark" className="mr-1">❌</span>
                )}
                Non (biens trop éloignés)
              </button>
              <button
                className="flex w-full items-center justify-center rounded border px-3 py-2 hover:bg-teal-50"
                onClick={() => { if (!isGestionLoading) { handleGestionTarget(true); setIsTargetGestionOpen(false); } }}
                disabled={isGestionLoading}
              >
                {isGestionLoading ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <span className="mr-1">🟡</span>
                )}
                Oui
              </button>
            </div>
          </div>
        </Popup>

        {/* Target Transaction Modal */}
        <Popup isOpen={isTargetTransactionOpen} onClose={() => setIsTargetTransactionOpen(false)} title={<span>Qualifier le ciblage Transaction de la SCI</span>} additionalClass="w-full max-w-lg">
          <div className="space-y-4 text-sm">
            <div className="font-semibold text-gray-700">{sciData ? getDisplayName(sciData) : `SCI ${siren}`}</div>
            <div className="space-y-2">
              <button
                className="flex w-full items-center justify-center rounded border px-3 py-2 hover:bg-indigo-50"
                onClick={() => { if (!isTransactionLoading) { handleTransactionTarget(false); setIsTargetTransactionOpen(false); } }}
                disabled={isTransactionLoading}
              >
                {isTransactionLoading ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <span role="img" aria-label="cross mark" className="mr-1">❌</span>
                )}
                Non (pas de potentiel identifié)
              </button>
              <button
                className="flex w-full items-center justify-center rounded border px-3 py-2 hover:bg-indigo-50"
                onClick={() => { if (!isTransactionLoading) { handleTransactionTarget(true); setIsTargetTransactionOpen(false); } }}
                disabled={isTransactionLoading}
              >
                {isTransactionLoading ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <span role="img" aria-label="house" className="mr-1">🏠</span>
                )}
                Oui (potentiel transaction)
              </button>
            </div>
          </div>
        </Popup>
      </div>
    </div>
  );
};

export default SirenSCIs;
